import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PulseButtonComponent } from '@h20-shared/pulse-components/pulse-button/pulse-button.component';
import { ToastService } from '@h20-shared/services/toast.service';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-copy-value-cell',
    imports: [PulseButtonComponent, FaIconComponent, RouterLink, TranslateModule],
    templateUrl: './copy-value-cell.component.html',
    styleUrl: './copy-value-cell.component.scss',
})
export class CopyValueCellComponent implements ICellRendererAngularComp {
    private readonly toast = inject(ToastService);
    private readonly translate = inject(TranslateService);

    protected readonly faCopy = faCopy;

    protected readonly cellConfig = signal<CopyableCellConfig | undefined>(undefined);
    protected readonly cellDisplayValue = signal<string | undefined>(undefined);
    protected readonly cellCopyValue = signal<unknown>(undefined);
    protected readonly routerLink = signal<readonly (string | number)[] | undefined>(undefined);

    agInit(params: ICellRendererParams<unknown, unknown> & CopyableCellParams): void {
        this.cellDisplayValue.set(params.valueFormatted ?? params.value?.toString() ?? '');
        this.cellCopyValue.set(params.value);
        this.cellConfig.set(params.copyConfig);
        this.routerLink.set(params.linkConfig?.getRouterLink(params));
    }
    refresh(params: ICellRendererParams<unknown, unknown> & CopyableCellParams): boolean {
        this.agInit(params);
        return true;
    }

    copyValue() {
        const copyText = this.cellCopyValue();
        const toast = this.cellConfig()?.toastOnCopy;
        if (copyText) {
            navigator.clipboard.writeText(copyText.toString()).then(() => {
                if (toast) {
                    this.toast.show({
                        heading: this.translate.instant(toast.heading),
                        body: this.translate.instant(toast.body),
                        type: 'success',
                        delay: 3000,
                    });
                }
            });
        }
    }
}

export type CopyableCellConfig = {
    /** i18n key of the accessible label for the copy button. */
    readonly copyButtonAccessibleLabel: string;
    /** i18n keys of the toast to be displayed when the value is copied. If not provided, no toast will be shown. */
    readonly toastOnCopy?: { readonly heading: string; readonly body: string };
};

export type CopyableCellParams = {
    readonly copyConfig: CopyableCellConfig;
    readonly linkConfig?: {
        readonly getRouterLink: (
            params: ICellRendererParams
        ) => readonly (string | number)[] | undefined;
    };
};
